import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Button, IconButton, Skeleton, Typography } from "@mui/material";

import useIsOwn from "../../../hooks/useIsOwn";
import { formatLocalizedTimeAgo } from "../../../utils/formatDate";

import { Service } from "../types/service.type";
import { ServiceTypeEnum } from "../enum/service.enum";
import { useJoinProgram } from "../api/mutations/useJoinProgram";
import { useRequestService } from "../api/mutations/useRequestService";
import { ServiceDetailsBackIcon } from "../assets/icons/service-details-icons";
import { useCancelServiceRequest } from "../api/mutations/useCancelServiceRequest";

import { CancelModal, useCancelModal } from "./CancelModal";
import { StripePayModal, useStripePayModal } from "./StripePayModal";
import { GO_SERVICE_QUERY_KEYS } from "../api/queryKeys";
import ServiceApprovalStatus from "./ServiceApprovalStatus";
import { getServiceRequestPageRoute } from "../../../routers/routeUtils";

export const GoHireHeader = ({
  service,
  isServiceLoading,
  handleRequestSubmittedModal,
}: {
  service?: Service;
  isServiceLoading?: boolean;
  handleRequestSubmittedModal: () => void;
}) => {
  const { t, i18n } = useTranslation("goService", { keyPrefix: "servicePage" });
  const navigate = useNavigate();
  const { openCancelModal, handleOpenCancelModal, handleCloseCancelModal } = useCancelModal();
  const { openStripePayModal, handleOpenStripePayModal, handleCloseStripePayModal, clientSecret, publishableKey } =
    useStripePayModal();

  const { mutate: requestService } = useRequestService(service?.id!, {
    onSuccess: handleRequestSubmittedModal,
  });

  const canRequestService = service?.canRequest;

  const handleRequestService = () => {
    if (!canRequestService) return;

    requestService();
  };

  const { mutate: cancelServiceRequest } = useCancelServiceRequest(service?.requestId!);

  const handleCancelServiceRequest = () => {
    if (!service?.requestId) return;
    cancelServiceRequest();
    handleCloseCancelModal();
  };

  const handleBack = () => {
    navigate(-1);
  };

  const { isOwn: isOwnService, isLoading: isOwnServiceLoading } = useIsOwn({
    userId: service?.user?.id,
    isUserDataLoading: isServiceLoading ?? false,
  });

  const isLoading = isServiceLoading || isOwnServiceLoading;

  const isService = service?.type === ServiceTypeEnum.OneTime;

  const { mutate: joinProgram, isPending: isJoinProgramPending } = useJoinProgram(service?.id!, {
    onSuccess: (data) => {
      const { clientSecret, publishableKey } = data.transaction || {};
      if (clientSecret && publishableKey) {
        handleOpenStripePayModal(clientSecret, publishableKey);
      }
    },
  });

  const handleJoinProgram = () => {
    if (!service?.id) return;
    joinProgram();
  };

  const handleViewRequest = () => {
    if (!service?.requestId) return;
    const serviceRequestPageRoute = getServiceRequestPageRoute(service.requestId);
    navigate(serviceRequestPageRoute);
  };

  if (isLoading) {
    return <GoHireHeaderLoader />;
  }

  return (
    <Box sx={styles.container}>
      <Box sx={styles.headerWrapper}>
        <Typography sx={styles.title}>
          <IconButton size="small" onClick={handleBack}>
            <ServiceDetailsBackIcon />
          </IconButton>
          {service?.name}
          {isOwnService && <ServiceApprovalStatus service={service} isLoading={isOwnServiceLoading} />}
        </Typography>
        <Box sx={styles.actionsContainer}>
          {/* {!isOwnService && canRequestService && !isService && (
            <>
              <Button onClick={handleJoinProgram} disabled={isLoading || isJoinProgramPending} variant="primary-md">
                {t("joinThisProgram")}
              </Button>
              <StripePayModal
                open={openStripePayModal}
                closeModal={handleCloseStripePayModal}
                clientSecret={clientSecret}
                publishableKey={publishableKey}
                queryKey={[...GO_SERVICE_QUERY_KEYS.service(service?.id!), { serviceId: service?.id! }] as string[]}
              />
            </>
          )} */}
          {/* {!isOwnService && canRequestService && isService && (
            <Button onClick={handleRequestService} disabled={isLoading} variant="primary-md">
              {t("requestThisService")}
            </Button>
          )} */}
          {!isOwnService && !canRequestService && isService && (
            <Button onClick={handleViewRequest} disabled={isLoading} variant="primary-md">
              {t("viewRequest")}
            </Button>
          )}
        </Box>
      </Box>
      {service?.expiryDate && (
        <Typography sx={styles.expiryText}>
          {t("expiresIn")} {formatLocalizedTimeAgo(service.expiryDate)}
        </Typography>
      )}
      <CancelModal
        open={openCancelModal}
        closeModal={handleCloseCancelModal}
        handelCancel={handleCancelServiceRequest}
        title={t("areYouSureToCancelThisRequest")}
      />
    </Box>
  );
};

export const GoHireHeaderLoader = () => {
  return (
    <Box sx={styles.container}>
      <Box sx={styles.headerWrapper}>
        <Box sx={styles.title}>
          <Skeleton variant="circular" width={32} height={32} />
          <Skeleton width={120} height={24} />
        </Box>
        <Box sx={styles.actionsContainer}>
          <Skeleton width={150} height={40} sx={{ borderRadius: "8px" }} />
        </Box>
      </Box>
      <Skeleton width={140} height={18} />
    </Box>
  );
};

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    borderRadius: "8px",
    border: "1px solid #EAECF0",
    background: "var(--White, #FFF)",
    padding: "20px",
  },
  headerWrapper: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: { xs: "flex-start", md: "center" },
    flexDirection: { xs: "column", md: "row" },
    gap: { xs: "16px", md: "0" },
  },
  title: {
    color: "var(--black-100, #1C1C1C)",
    fontFamily: "Montserrat",
    fontSize: "20px",
    fontStyle: "normal",
    fontWeight: 800,
    lineHeight: "normal",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  expiryText: {
    fontFamily: "Montserrat",
    overflow: "hidden",
    color: "var(--Gray-600, #475467)",
    textOverflow: "ellipsis",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "18px",
  },
  actionsContainer: {
    display: "flex",
    gap: "8px",
    width: { xs: "100%", md: "auto" },
    "& > button": {
      width: { xs: "100%", md: "auto" },
    },
  },
};
